import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath, focusHref } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, SkeletonList, QueryError } from '../../../../components/ui';

export interface EscalationsWidgetProps {
  escalations: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

export function EscalationsWidget({ escalations }: EscalationsWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const count = escalations.data?.actions.length ?? 0;

  return (
    <DataCard
      title="Needs Your Attention"
      badge={count > 0 ? <StatusBadge status="warning" label={String(count)} size="xs" /> : undefined}
    >
      {escalations.isLoading ? <SkeletonList rows={3} /> : escalations.isError ? <QueryError error={escalations.error} onRetry={() => escalations.refetch()} compact /> : (
        <div className="space-y-2">
          {escalations.data?.actions.slice(0, 5).map((a: any) => (
            <button type="button" key={a.id} onClick={() => navigate(focusHref(basePath, '/actions', a.id))} className={clsx('w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-surface-1 hover:border-border-strong cursor-pointer', a.severity === 'critical' ? 'border-danger/30 bg-danger/3' : a.severity === 'high' ? 'border-warn/30 bg-warn/3' : 'border-border')}>
              <StatusBadge status={a.severity === 'critical' ? 'critical' : a.severity === 'high' ? 'warning' : 'info'} label={a.severity} size="xs" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink">{a.title}</p>
                <p className="mt-0.5 truncate text-2xs text-ink-dim">{a.description}</p>
              </div>
              <span className="shrink-0 text-2xs text-ink-dim">{a.channel}</span>
            </button>
          ))}
          {escalations.data && escalations.data.actions.length === 0 && escalations.data.pendingCount === null && (
            <div className="py-4 text-center text-2xs text-ink-dim">Nothing to show yet: Waypoint can't see all of your email and billing, so it can't say all is clear.</div>
          )}
          {(!escalations.data || (escalations.data.actions.length === 0 && escalations.data.pendingCount !== null)) && (
            <div className="py-4 text-center text-2xs text-ink-dim">All clear — no escalations right now</div>
          )}
        </div>
      )}
    </DataCard>
  );
}
